import { z } from 'zod'
import { browserAssetUrlSchema } from '../helpers/asset-ref.js'
import { DIRECTORY_SERVICES } from '../constants/vendor-directory.js'
import { vendorPriceUnitSchema } from '../enums/vendor-price-unit.enum.js'

/** A vendor's price in a category, as the directory shows it. A price on request never carries a number. */
export const directoryPriceDtoSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('shown'), cents: z.number().int().nonnegative(), from: z.boolean(), unit: vendorPriceUnitSchema }),
  z.object({ kind: z.literal('on_request') }),
])

export type DirectoryPriceDto = z.infer<typeof directoryPriceDtoSchema>

/** One of the venue's vendors, as couples and planners browse them (docs/vendor-growth.spec.md, "Couples find vendors"). */
export const directoryVendorDtoSchema = z.object({
  vendorId: z.string().uuid(),
  name: z.string(),
  logoUrl: browserAssetUrlSchema.nullable(),
  /** What the venue approved it for and it still offers, in the order the app lists services. Never furniture. */
  services: z.array(z.enum(DIRECTORY_SERVICES)),
  recommendedByVenue: z.boolean(),
  /** In the category asked about; null without one, or when it lists nothing there. */
  price: directoryPriceDtoSchema.nullable(),
  /** Under a price filter: a priced match, or listed after the matches as on request. Null without a filter. */
  match: z.enum(['priced', 'on_request']).nullable(),
})

export type DirectoryVendorDto = z.infer<typeof directoryVendorDtoSchema>

/**
 * A venue's vendors, for an event there: recommended by the venue first, then
 * those who have done the most events there, then by name. Under a price
 * filter, priced matches first and vendors with a price on request after.
 */
export const vendorDirectoryDtoSchema = z.object({
  venueName: z.string(),
  vendors: z.array(directoryVendorDtoSchema),
  /** The units prices are shown in, in the category asked about, most used first. Empty without a category. */
  units: z.array(vendorPriceUnitSchema),
})

export type VendorDirectoryDto = z.infer<typeof vendorDirectoryDtoSchema>
